import { WhatsAppLogo } from "assets/whatsapp"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { cn } from "design-system/utils"
import { Check, Globe, Pencil } from "lucide-react"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { GoogleIcon } from "modules/shared/icons/google"
import { InstagramIcon } from "modules/shared/icons/instagram"
import { OutlookIcon } from "modules/shared/icons/outlook"
import { useRef, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  CampaignDetailResponseDataSchema,
  CampaignStatusEnum,
  CampaignTriggerTypeEnum,
} from "schema"
import { z } from "zod"
import { CampaignFlowBadge } from "./CampaignFlowBadge"
import { CampaignStatusBadge } from "./CampaignStatusBadge"

type Props = {
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>
}

function MetaBadge({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Badge variant="outline" className="text-muted-foreground px-2 py-1">
      <div className="flex flex-row items-center gap-2">
        {icon}
        <span>{label}</span>
      </div>
    </Badge>
  )
}

function ChannelBadge({ channel }: { channel: Props["campaign"]["channel"] }) {
  switch (channel) {
    case "Whatsapp":
      return <MetaBadge icon={<WhatsAppLogo className="size-4" />} label="WhatsApp" />
    case "Web":
    case "Api":
      return <MetaBadge icon={<Globe size={14} />} label="Web" />
    case "Instagram":
      return <MetaBadge icon={<InstagramIcon className="size-4" />} label="Instagram" />
    case "Gmail":
      return <MetaBadge icon={<GoogleIcon className="size-4" />} label="Gmail" />
    case "Outlook":
      return <MetaBadge icon={<OutlookIcon className="size-4" />} label="Outlook" />
    case "Email":
    case null:
      return null
  }
}

function CampaignNameEditor() {
  const { t } = useTranslation()
  const form = useFormContext<z.infer<typeof UpdateCampaignSchemaFrontend>>()
  const name = form.watch("name") ?? ""
  const [draftName, setDraftName] = useState(name)
  const [isEditing, setIsEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const nameIsSet = name.trim().length > 0

  function onEdit() {
    setDraftName(name)
    setIsEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  function onCommit() {
    setIsEditing(false)
    const trimmed = draftName.trim()
    if (trimmed.length === 0) return
    if (trimmed === name) return
    form.setValue("name", trimmed, { shouldDirty: true })
  }

  function onCancel() {
    setDraftName(name)
    setIsEditing(false)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    if (e.key === "Escape") onCancel()
  }

  if (isEditing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault()
          onCommit()
        }}
        onKeyDown={onKeyDown}
        className="flex items-center gap-1"
      >
        <Input
          ref={inputRef}
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          className="min-w-52 text-xl font-semibold shrink-0 outline-0! ring-0! focus-visible:ring-0! focus-visible:ring-offset-0!"
          placeholder={t("campaigns.builder.name.placeholder")}
        />
        <Button type="submit" variant="ghost" size="icon" className="shrink-0">
          <Check className="w-4 h-4" />
        </Button>
      </form>
    )
  }

  return (
    <div className="flex items-center gap-3">
      <h1 className={cn("text-xl font-semibold", !nameIsSet && "text-muted-foreground")}>
        {!nameIsSet ? t("campaigns.untitled") : name}
      </h1>
      <Button
        variant="ghost"
        size="icon"
        onClick={onEdit}
        className="text-muted-foreground/50 focus-visible:text-foreground"
      >
        <Pencil className="w-4 h-4" />
      </Button>
    </div>
  )
}

export function CampaignHeader({ campaign }: Props) {
  // Manual campaigns are activated by their first launch, so only a system pause is worth showing
  const shouldShowStatus =
    campaign.trigger !== CampaignTriggerTypeEnum.Manual ||
    campaign.status === CampaignStatusEnum.Paused

  return (
    <div className="flex flex-col items-start gap-3">
      <CampaignNameEditor />
      <div className="flex flex-row gap-2 items-center">
        {shouldShowStatus && <CampaignStatusBadge status={campaign.status} />}
        {campaign.flow && <CampaignFlowBadge flow={campaign.flow} />}
        <ChannelBadge channel={campaign.channel} />
      </div>
    </div>
  )
}
